import { t } from '@avalara/bat-apps-shared/i18n';
import { getFieldErrorMessage } from '@avalara/bat-apps-shared/lib/form-validation';
import type { Dispatch } from '@avalara/types/actions';
import type { InputEvent, NewAccountFormField } from '@avalara/types/index';
import type { State } from '@avalara/types/reducer-states';
import { Fragment } from 'react';
import { type MapDispatchToPropsNonObject, connect } from 'react-redux';

import type { OfferFlow } from '../../../../../types/offers';
import { FieldErrorMessage, SelectInput } from '../../../components/styled/components/sign-up';
import { isFieldVisitedAndInvalid } from '../../../lib/shared-signup';
import { updateProductAnalytics } from '../../../lib/side-effects/analytics';
import { updateSignupForm } from '../../../lib/side-effects/signup-form';
import { clearFieldForValidation } from '../../../store/shared-signup';

import type { Props as InputProps } from './form-input';

type StateProps = {
  value: string | null;
  fieldMissingOrInvalid: boolean;
  disabled: boolean;
  offerFlow?: OfferFlow;
};

type DispatchProps = {
  onClearFieldForValidation: (e: InputEvent, fieldName: NewAccountFormField) => void;
  onFormChanged: (e: InputEvent, fieldName: NewAccountFormField) => void;
  onCreateAccountStart: (offerFlow?: OfferFlow) => void;
};

type FieldValue = {
  display?: string;
  value: string;
};

type OwnProps = InputProps & {
  fieldValues?: Array<FieldValue>; // Will default to value as display if display is not passed
};
type Props = OwnProps & StateProps & DispatchProps;

function handleFocus(props: Props, e: InputEvent) {
  if (props.validateDuringInput) {
    props.onClearFieldForValidation(e, props.fieldName);
  }
}

const FormSelectComponent = (props: Props) => {
  const selectOption = (f: FieldValue) => (f.display != null ? f.display : f.value);

  return (
    <Fragment>
      <SelectInput
        disabled={props.disabled}
        name={props.fieldName}
        id={props.fieldName}
        aria-label={props.fieldName}
        data-ref={props.fieldName}
        onChange={(e: InputEvent) => {
          props.onFormChanged(e, props.fieldName);
        }}
        onBlur={(e: InputEvent) => {
          props.onClearFieldForValidation(e, props.fieldName);
        }}
        onFocus={handleFocus.bind(this, props)}
        value={props.value || ''}
      >
        <option value='' disabled>
          {t('client.general.select')}
        </option>
        {props.fieldValues?.map((f) => (
          <option key={`selectOption-${f.value}`} value={f.value || f.display}>
            {selectOption(f)}
          </option>
        ))}
      </SelectInput>
      {props.fieldMissingOrInvalid && (
        <FieldErrorMessage className={`field-error-message ${props.addPadding != null ? 'margin-all-none' : ''}`}>
          <p>{getFieldErrorMessage(props.fieldName, props.value)}</p>
        </FieldErrorMessage>
      )}
    </Fragment>
  );
};

const mapStateToProps = (state: State, ownProps: OwnProps): StateProps => {
  const {
    sharedSignup,
    offer: { offerFlow },
  } = state;
  const { fieldName } = ownProps;

  return {
    value: sharedSignup.signupForm[fieldName] || '',
    fieldMissingOrInvalid: isFieldVisitedAndInvalid(sharedSignup, fieldName),
    disabled: sharedSignup.disableForm || !!sharedSignup.readOnlyFields?.includes(fieldName),
    offerFlow,
  };
};

const mapDispatchToProps = (dispatch: Dispatch): DispatchProps => ({
  onClearFieldForValidation: (_: InputEvent, field: NewAccountFormField) => {
    dispatch(clearFieldForValidation(field));
  },
  onCreateAccountStart: (offerFlow?: OfferFlow) => {
    dispatch(updateProductAnalytics('account_start', !!offerFlow));
  },

  // TODO(Investigate): We used to persist the entire InputEvent to the signupFormChanged function,
  // but a recent change seems to have introduced a race condition (repro'd by rapid typing :-))
  // that leads to us trying to access the event's properties after it's been nulled out.
  // See https://reactjs.org/docs/events.html#event-pooling for a better description.
  onFormChanged: (event: InputEvent, field: NewAccountFormField) => {
    dispatch(updateSignupForm(field, event.target.value));
  },
});

export default connect(
  mapStateToProps,
  mapDispatchToProps as MapDispatchToPropsNonObject<DispatchProps, OwnProps>,
)(FormSelectComponent);
